'use client'

/* eslint-disable react/hook-use-state */
import { arrayMove } from '@dnd-kit/sortable'
import { noop } from 'lodash'
import { createContext, ReactNode, useMemo, useState } from 'react'

import { UiState } from '@marketplace-web/shared/ui-state-data'
import { useLatestCallback } from '@marketplace-web/shared/ui-helpers'
import { ImageOrientation } from 'constants/image-orientation'
import { MediaUploadPhotoModel } from 'types/models/media-upload-photo'
import { PhotoTipModel } from 'types/models/photo-tip'

import { MediaSelectPhotoOrientation } from '../types'
import { MediaSelectContextType } from '../types/context'

export const MediaSelectContext = createContext<MediaSelectContextType>({
  photos: [],
  photoTipsStateWithSetter: [[], noop],
  uiStateWithSetter: [UiState.Idle, noop],
  photosCount: 0,
  photoTempIds: [],
  removePhoto: noop,
  setPhotoOrientation: noop,
  changePhotoOrder: noop,
  addPhotos: noop,
})

export const MediaSelectContextProvider = ({ children }: { children: ReactNode }) => {
  const [photos, setPhotos] = useState<Array<MediaUploadPhotoModel>>([])
  const photoTipsStateWithSetter = useState<Array<PhotoTipModel>>([])
  const uiStateWithSetter = useState(UiState.Idle)
  const photoTempIds = photos.reduce<Array<string>>((acc, photo) => {
    if (photo.tempUuid) acc.push(photo.tempUuid)

    return acc
  }, [])

  const addPhotos = useLatestCallback((photosToBeAdded: Array<MediaUploadPhotoModel>) => {
    if (!photosToBeAdded.length) return

    setPhotos(prev => [...prev, ...photosToBeAdded])
  })

  const removePhoto = (photoId: number) => {
    setPhotos(prevPhotos => prevPhotos.filter(photo => photo.id !== photoId))
  }

  const changePhotoOrder = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    setPhotos(prev => arrayMove(prev, oldIndex, newIndex))
  }

  const setPhotoOrientation = (photoOrientation: MediaSelectPhotoOrientation) => {
    setPhotos(prev =>
      prev.map(photo =>
        photo.id === photoOrientation.id
          ? { ...photo, orientation: photoOrientation.orientation || ImageOrientation.Degree0 }
          : photo,
      ),
    )
  }

  const providerValue = useMemo(
    () => ({
      photos,
      photoTipsStateWithSetter,
      uiStateWithSetter,
      photosCount: photos.length,
      photoTempIds,
      removePhoto,
      addPhotos,
      setPhotoOrientation,
      changePhotoOrder,
    }),
    [addPhotos, photoTempIds, photos, photoTipsStateWithSetter, uiStateWithSetter],
  )

  return <MediaSelectContext.Provider value={providerValue}>{children}</MediaSelectContext.Provider>
}
